import { Box, TextField, Typography } from "@mui/material";
import React from "react";
import {
  reportBodyCellTdSx,
  reportCellBackground,
  priceCellInnerSx,
  reportTableTheme,
} from "./reportTableTheme";
import { decodeHtmlEntities } from "./utils/decodeHtmlEntities";
import { ResolvedReportCell } from "./utils/resolveOperatorCell";

interface Props {
  cell: ResolvedReportCell | null;
  variant?: "competitor" | "ourPrice";
  isEditing?: boolean;
  editValue?: string;
  onEditValueChange?: (value: string) => void;
}

const emptyCellSx = {
  ...reportBodyCellTdSx,
  ...reportCellBackground("#ffffff"),
};

const priceInputSx = {
  "& .MuiInputBase-input": {
    fontSize: reportTableTheme.priceFontSize,
    fontWeight: 700,
    py: 0.25,
    px: 0.5,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#bdbdbd",
  },
};

const cellContentSx = {
  ...priceCellInnerSx,
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "center",
  gap: "2px 6px",
  width: "100%",
} as const;

const deltaTextSx = {
  fontSize: reportTableTheme.deltaFontSize,
  fontWeight: 500,
  whiteSpace: "nowrap",
  flexShrink: 0,
} as const;

export function TicketGridReportCell({
  cell,
  variant = "competitor",
  isEditing = false,
  editValue = "",
  onEditValueChange,
}: Props) {
  if (isEditing) {
    const backgroundColor =
      variant === "competitor" && cell ? cell.backgroundColor : "#ffffff";

    return (
      <Box
        component="td"
        sx={{
          ...emptyCellSx,
          ...reportCellBackground(backgroundColor),
          textAlign: variant === "ourPrice" ? "right" : "center",
        }}
      >
        <Box
          sx={{
            ...priceCellInnerSx,
            display: "flex",
            alignItems: "center",
            justifyContent: variant === "ourPrice" ? "flex-end" : "center",
          }}
        >
          <TextField
            value={editValue}
            onChange={(event) => onEditValueChange?.(event.target.value)}
            size="small"
            variant="outlined"
            inputMode="numeric"
            sx={{
              ...priceInputSx,
              width: variant === "ourPrice" ? 88 : 80,
            }}
          />
        </Box>
      </Box>
    );
  }

  if (!cell) {
    return (
      <Box
        component="td"
        sx={{
          ...emptyCellSx,
          textAlign: variant === "ourPrice" ? "right" : "center",
        }}
      >
        <Box sx={priceCellInnerSx} />
      </Box>
    );
  }

  if (variant === "ourPrice") {
    return (
      <Box
        component="td"
        sx={{
          ...emptyCellSx,
          textAlign: "right",
        }}
      >
        <Box
          sx={{
            ...priceCellInnerSx,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          {cell.price !== null ? (
            <Typography
              component="span"
              sx={{
                fontSize: reportTableTheme.priceFontSize,
                fontWeight: 700,
                color: cell.priceColor,
              }}
            >
              {cell.price}
            </Typography>
          ) : null}
        </Box>
      </Box>
    );
  }

  const delta = cell.delta ? decodeHtmlEntities(cell.delta) : "";
  const backgroundColor = cell.backgroundColor;
  const hasPrice = cell.price !== null;

  return (
    <Box
      component="td"
      sx={{
        ...reportBodyCellTdSx,
        ...reportCellBackground(backgroundColor),
      }}
    >
      {hasPrice || delta ? (
        <Box sx={cellContentSx}>
          {hasPrice ? (
            <Typography
              component="span"
              sx={{
                fontSize: reportTableTheme.priceFontSize,
                fontWeight: 700,
                color: cell.priceColor,
                flexShrink: 0,
              }}
            >
              {cell.price}
            </Typography>
          ) : null}
          {delta ? (
            <Typography
              component="span"
              sx={{
                ...deltaTextSx,
                color: cell.deltaColor,
              }}
            >
              {delta}
            </Typography>
          ) : null}
        </Box>
      ) : (
        <Box sx={priceCellInnerSx} />
      )}
    </Box>
  );
}
